import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { computationOptions } from "#app/computationOptions";
import { colors } from "#app/theme/v1/colors";
import { useCompany } from "#modules/company/view/useCompany";
import { useOpenOptimizationDetail } from "#modules/optimizations/view/OptimizationDetailContext";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { AnalyzeError } from "#modules/quoteRequestLines/AnalyzeError";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { PartDimensionsModal } from "#modules/quoteRequestLines/view/PartDimensionsModal";
import { CostCollapsable } from "#modules/quoteRequestLines/views/DetailsPanel/CostCollapsable";
import { MaterialCost } from "#modules/quoteRequestLines/views/DetailsPanel/Material/MaterialCost";
import { Stack } from "#shared/view/components/Stack";
import { isJobProcessing } from "shared";
import { checkPartDimensions, computeMaterialPrice } from "shared";

export const MaterialSection = ({
  quoteRequestLine,
  hasChildren,
  isChild,
}: {
  quoteRequestLine: QuoteRequestLineDto;
  hasChildren: boolean;
  isChild: boolean;
}) => {
  const { t } = useTranslation();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  const openOptimizationDetail = useOpenOptimizationDetail();
  const { company } = useCompany();
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequestLine.quoteRequestId);

  const layout = quoteRequestLine.layout;

  if (!layout || !company) {
    return null;
  }

  const { partMaterialCost } = computeMaterialPrice(
    {
      quoteRequestLine,
      optimizedMaterialCosts,
    },
    computationOptions,
  );

  const showDimensionsAlert = checkPartDimensions(quoteRequestLine.part).hasError;

  return (
    <CostCollapsable
      title={t("quoteRequestLine.materialCost")}
      color={colors.violet[5]}
      value={showDimensionsAlert ? 0 : partMaterialCost}
      loading={
        !!quoteRequestLine.predictionStatus && isJobProcessing(quoteRequestLine.predictionStatus)
      }
      defaultOpened
      data-testid="material-section"
    >
      {showDimensionsAlert ? (
        <Stack gap={0}>
          <AnalyzeError
            justify="start"
            errorText={t("material.invalidDimensions")}
            retryText={t("material.invalidDimensionsDescription")}
            retryAction={openModal}
          />
          <PartDimensionsModal
            part={quoteRequestLine.part}
            quoteRequestLine={quoteRequestLine}
            partDimensionsModalOpened={modalOpened}
            closePartDimensionsModal={closeModal}
          />
        </Stack>
      ) : (
        <MaterialCost
          quoteRequestLine={{ ...quoteRequestLine, layout }}
          nestingMode={company.companySettings.nestingMode}
          companyPricingMode={company.companySettings.materialPricingMode}
          hasChildren={hasChildren}
          isChild={isChild}
          openOptimizationDetail={openOptimizationDetail}
        />
      )}
    </CostCollapsable>
  );
};
